'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';

import { buttonClasses, Card } from '@/components/ui';
import {
  readLocalToolRuns,
  upsertLocalToolRun,
  writeLocalToolRuns,
  type LocalToolRun,
} from '@/lib/free-tools/local-tool-runs';

interface LocalToolRunsValue {
  runs: LocalToolRun[];
  record: (run: { title: string; summary: string; state: Record<string, unknown> }) => void;
  /** The next `record` starts a new entry instead of updating the current one. */
  startNew: () => void;
  restore: (run: LocalToolRun) => void;
  remove: (id: string) => void;
  clear: () => void;
  restoreRequest: { state: Record<string, unknown>; nonce: number } | null;
}

const LocalToolRunsContext = createContext<LocalToolRunsValue | null>(null);

/**
 * Wraps a browser-only tool on `/dashboard/tools/<slug>`. Outside this provider (the
 * public `/tools` pages) the hooks below are no-ops, so anonymous visitors' input is
 * never written to storage.
 */
export function LocalToolRunsProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [runs, setRuns] = useState<LocalToolRun[]>([]);
  const [restoreRequest, setRestoreRequest] = useState<LocalToolRunsValue['restoreRequest']>(null);
  const currentId = useRef<string | null>(null);

  useEffect(() => setRuns(readLocalToolRuns(slug)), [slug]);

  const persist = useCallback(
    (update: (prev: LocalToolRun[]) => LocalToolRun[]) =>
      setRuns((prev) => {
        const next = update(prev);
        writeLocalToolRuns(slug, next);
        return next;
      }),
    [slug]
  );

  // Stable callbacks: tool clients depend on `record`/`startNew` in effects, and an
  // identity that changed with every save would re-record on every save.
  const record = useCallback<LocalToolRunsValue['record']>(
    ({ title, summary, state }) => {
      currentId.current ??= `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const id = currentId.current;
      persist((prev) => upsertLocalToolRun(prev, { id, at: new Date().toISOString(), title, summary, state }));
    },
    [persist]
  );

  const startNew = useCallback(() => {
    currentId.current = null;
  }, []);

  const restore = useCallback((run: LocalToolRun) => {
    if (!run.state) return;
    // Edits after a restore update that entry rather than spawning a near-duplicate.
    currentId.current = run.id;
    setRestoreRequest({ state: run.state, nonce: Date.now() });
  }, []);

  const remove = useCallback(
    (id: string) => {
      if (currentId.current === id) currentId.current = null;
      persist((prev) => prev.filter((r) => r.id !== id));
    },
    [persist]
  );

  const clear = useCallback(() => {
    currentId.current = null;
    persist(() => []);
  }, [persist]);

  const value = useMemo<LocalToolRunsValue>(
    () => ({ runs, restoreRequest, record, startNew, restore, remove, clear }),
    [runs, restoreRequest, record, startNew, restore, remove, clear]
  );

  return <LocalToolRunsContext.Provider value={value}>{children}</LocalToolRunsContext.Provider>;
}

const RECORD_DEBOUNCE_MS = 1500;

/**
 * Called by a browser-only tool client with its current run (or null when there is
 * nothing worth keeping, e.g. empty input). Debounced, so typing updates one entry.
 * `restore` receives a saved run's `state` when the user picks it from Recent.
 */
export function useLocalToolRun(
  run: { title: string; summary: string; state: Record<string, unknown> } | null,
  restore: (state: Record<string, unknown>) => void
) {
  const ctx = useContext(LocalToolRunsContext);
  const record = ctx?.record;
  const startNew = ctx?.startNew;
  const restoreRequest = ctx?.restoreRequest ?? null;
  const restoreRef = useRef(restore);
  const serialized = run ? JSON.stringify(run) : null;

  useEffect(() => {
    restoreRef.current = restore;
  });

  useEffect(() => {
    if (!record || !startNew) return;
    if (!serialized) {
      startNew();
      return;
    }
    // A restored run re-records under its own id, which just moves it to the top.
    const timer = window.setTimeout(() => record(JSON.parse(serialized)), RECORD_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [serialized, record, startNew]);

  useEffect(() => {
    if (!restoreRequest) return;
    restoreRef.current(restoreRequest.state);
  }, [restoreRequest]);
}

function formatAgo(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString();
}

/** The Recent panel for browser-only tools. Must sit inside `LocalToolRunsProvider`. */
export function LocalRecentRuns() {
  const ctx = useContext(LocalToolRunsContext);
  if (!ctx) return null;
  const { runs, restore, remove, clear } = ctx;

  return (
    <section aria-labelledby="recent-tool-runs" className="mt-10">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 id="recent-tool-runs" className="text-sm font-semibold text-ink">
            Recent
          </h2>
          <p className="mt-0.5 text-[13px] text-ink-2">
            Your last 10 runs, saved in this browser only. Nothing is uploaded.
          </p>
        </div>
        {runs.length > 0 ? (
          <button type="button" onClick={clear} className="text-[12px] text-ink-2 hover:text-ink">
            Clear all
          </button>
        ) : null}
      </div>
      <Card className="mt-3">
        {runs.length === 0 ? (
          <p className="p-4 text-[13px] text-ink-2">Nothing yet. Runs you make here will show up in this list.</p>
        ) : (
          <ul className="divide-y divide-line">
            {runs.map((run) => (
              <li key={run.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:gap-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink" title={run.title}>
                    {run.title}
                  </p>
                  <p className="mt-0.5 truncate text-[12px] text-ink-3">
                    {formatAgo(run.at)} · {run.summary}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {run.state ? (
                    <button
                      type="button"
                      onClick={() => {
                        restore(run);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={buttonClasses('secondary', 'sm')}
                    >
                      <RotateCcw aria-hidden className="h-3.5 w-3.5" />
                      Restore
                    </button>
                  ) : (
                    <span className="text-[12px] text-ink-3">Too large to restore</span>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(run.id)}
                    aria-label={`Remove ${run.title}`}
                    className={buttonClasses('ghost', 'sm')}
                  >
                    <Trash2 aria-hidden className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
